import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React from "react";
import { useNavigate } from "react-router-dom";
import { store } from "../../store";
import { Chip, Box, Typography, Stack, Tooltip, alpha } from "@mui/material";
import {
  WaterDrop,
  ElectricBolt,
  Tv,
  DevicesOther,
  Cancel,
  Inventory2,
  Business,
  Home,
} from "@mui/icons-material";

// Icône selon le type de device
const getDeviceIcon = (deviceType) => {
  const type = deviceType?.toLowerCase() || "";
  if (type.includes("water") || type.includes("eau")) {
    return <WaterDrop sx={{ fontSize: 16 }} />;
  }
  if (type.includes("elec") || type.includes("power") || type.includes("énergie")) {
    return <ElectricBolt sx={{ fontSize: 16 }} />;
  }
  if (type.includes("screen") || type.includes("écran") || type.includes("display")) {
    return <Tv sx={{ fontSize: 16 }} />;
  }
  return <DevicesOther sx={{ fontSize: 16 }} />;
};

// Couleur selon le type de device (tons sobres)
const getDeviceColor = (deviceType) => {
  const type = deviceType?.toLowerCase() || "";
  if (type.includes("water") || type.includes("eau")) return "#6b9dad";
  if (type.includes("elec") || type.includes("power") || type.includes("énergie")) return "#c4a574";
  if (type.includes("screen") || type.includes("écran") || type.includes("display")) return "#8a9ba8";
  return "#7a8a7a";
};

export default function PacksTable({ filteredPacks }) {
  const allPacks = useSelector((state) => state.packs);
  const devices = useSelector((state) => state.devices);
  const residences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);

  // Utiliser les packs filtrés si fournis, sinon tous les packs
  const packs = filteredPacks || allPacks;

  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Inventory2 sx={{ color: "#78a882", fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#ffffff" }}>
              {value}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "devices",
      label: t("common.devices"),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value || value.length === 0) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DevicesOther sx={{ color: "#9e9e9e", fontSize: 20 }} />
                <Typography sx={{ color: "#9e9e9e", fontWeight: 500 }}>
                  0 {t("common.devices").toLowerCase()}
                </Typography>
              </Box>
            );
          }

          const deviceCount = value.length;

          return (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <DevicesOther sx={{ color: "#78a882", fontSize: 20 }} />
                <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                  {deviceCount} {t("common.devices").toLowerCase()}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {value.slice(0, 4).map((device, idx) => {
                  const deviceDetails = devices.find(
                    (d) => d.dev_eui === device.dev_eui
                  );
                  const deviceType =
                    deviceDetails?.device_brand?.device_type?.name ||
                    deviceDetails?.device_type?.name ||
                    "";
                  const color = getDeviceColor(deviceType);

                  return (
                    <Tooltip
                      key={idx}
                      title={`${device.dev_eui}${deviceType ? ` (${deviceType})` : ""}`}
                    >
                      <Chip
                        icon={getDeviceIcon(deviceType)}
                        label={deviceDetails?.name || device.dev_eui.slice(-6)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: alpha(color, 0.5),
                          color: color,
                          "& .MuiChip-icon": { color: color },
                          fontSize: "0.75rem",
                        }}
                      />
                    </Tooltip>
                  );
                })}
                {value.length > 4 && (
                  <Chip
                    label={`+${value.length - 4}`}
                    size="small"
                    sx={{
                      backgroundColor: alpha("#7a8a7a", 0.15),
                      color: "#9aa89a",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Stack>
            </Box>
          );
        },
      },
    },
    {
      name: "residence",
      label: t("common.residence"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const packId = tableMeta.rowData[0];
          const assignedResidence = residences.find(
            (residence) => residence.pack_id === packId
          );

          if (assignedResidence) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Home sx={{ color: "#6b9dad", fontSize: 20 }} />
                <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                  {assignedResidence.name}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Cancel sx={{ color: "#c4a574", fontSize: 20 }} />
                <Typography sx={{ color: "#c4a574", fontWeight: 500 }}>
                  {t("common.not_assigned") || "Non assigné"}
                </Typography>
              </Box>
            );
          }
        },
      },
    },
    {
      name: "establishment",
      label: t("common.establishment"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const packId = tableMeta.rowData[0];
          // Trouver la résidence associée au pack
          const assignedResidence = residences.find(
            (residence) => residence.pack_id === packId
          );

          if (!assignedResidence) {
            return (
              <Typography variant="body2" sx={{ color: "#7a8a7a" }}>
                —
              </Typography>
            );
          }

          // Trouver l'établissement de la résidence
          const establishment = establishments.find(
            (e) => parseInt(e.id) === parseInt(assignedResidence.establishment_id)
          );

          if (establishment) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Business sx={{ color: "#9a8878", fontSize: 20 }} />
                <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                  {establishment.name}
                </Typography>
              </Box>
            );
          } else {
            return (
              <Typography variant="body2" sx={{ color: "#7a8a7a" }}>
                —
              </Typography>
            );
          }
        },
      },
    },
  ];

  const [selectedPacks, setSelectedpacks] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);

  const tableActions = [
    {
      name: t("common.delete_selected", {
        name: t("common.packs"),
        count: selectedPacks.length,
      }),
      icon: "Delete",
      action: () => {
        store.dispatch({
          type: "deleteDialog/update",
          payload: {
            open: true,
            type: "packs",
            records: selectedPacks,
          },
        });
      },
    },
  ];

  return (
    <DataTableBase
      columns={columns}
      data={packs}
      title={t("common.packs")}
      id="packs-table"
      name="packs"
      pageSize={10}
      selectedItems={selectedPacks}
      setSelectedItems={setSelectedpacks}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      selectableRows="multiple"
      actions={tableActions}
      sortOrder="dev_eui"
      onRowClick={(rowData, rowMeta) => {
        // Utiliser rowMeta.dataIndex pour accéder aux données originales
        const pack = packs[rowMeta.dataIndex];
        if (pack && pack.id !== undefined) {
          navigate(`/packs/${pack.id}`);
        }
      }}
    />
  );
}
